// ============================================
// New Trip Request — Client Portal (org contact)
// Writes jobs + job_legs (not trips). SOL-20260716-client-org-portal
// ============================================

import { useState, useEffect } from 'react';
import {
  Card, Typography, Form, Input, InputNumber, DatePicker, TimePicker,
  Button, Select, message, Steps, Result, Space, Divider
} from 'antd';
import {
  CarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  getCurrentOrgId,
  resolveTripGeopointsStrict,
  TripGeocodeError,
  AddressField,
  buildClientTransferLegTimes,
  parseGeoPoint,
} from '@ubus/shared';
import type { AddressCoordinates } from '@ubus/shared';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface PassengerGroup {
  key: number;
  name: string;
  count: number;
  lead_name: string;
  lead_phone: string;
}

function TripBooking() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [createdRequestId, setCreatedRequestId] = useState<string | null>(null);
  const [pickupCoords, setPickupCoords] = useState<AddressCoordinates | null>(null);
  const [dropoffCoords, setDropoffCoords] = useState<AddressCoordinates | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [passengerGroups, setPassengerGroups] = useState<PassengerGroup[]>([
    { key: 0, name: t('booking.mainGroup'), count: 20, lead_name: '', lead_phone: '' }
  ]);

  useEffect(() => {
    getCurrentOrgId().then(async (id) => {
      setOrgId(id);
      if (!id) return;
      const { data } = await supabase
        .from('organizations')
        .select('contact_person, contact_phone')
        .eq('id', id)
        .maybeSingle();
      if (data) {
        form.setFieldsValue({
          contact_person: data.contact_person || undefined,
          contact_phone: data.contact_phone || undefined,
        });
      }
    });
  }, [form]);

  const totalPassengers = passengerGroups.reduce((sum, g) => sum + g.count, 0);

  const vehicleOptions = [
    { label: t('booking.vehicleStandard'), value: 'standard' },
    { label: t('booking.vehicleMini'), value: 'mini' },
    { label: t('booking.vehicleCoach'), value: 'coach' },
    { label: t('booking.vehicleAny'), value: 'any' },
  ];

  const vehicleLabel = (value: string) =>
    vehicleOptions.find((o) => o.value === value)?.label ?? value;

  const addGroup = () => {
    setPassengerGroups(prev => [
      ...prev,
      {
        key: Date.now(),
        name: t('booking.groupN', { n: prev.length + 1 }),
        count: 20,
        lead_name: '',
        lead_phone: ''
      }
    ]);
  };

  const removeGroup = (key: number) => {
    if (passengerGroups.length <= 1) return;
    setPassengerGroups(prev => prev.filter(g => g.key !== key));
  };

  const updateGroup = (key: number, field: keyof PassengerGroup, value: string | number) => {
    setPassengerGroups(prev =>
      prev.map(g => (g.key === key ? { ...g, [field]: value } : g))
    );
  };

  const stepFieldNames: (string | string[])[][] = [
    ['date', 'pickup_time', 'vehicle_preference', 'contact_person', 'contact_phone'],
    ['pickup_address', 'dropoff_address'],
    [],
    ['date', 'pickup_time', 'pickup_address', 'dropoff_address', 'contact_person', 'contact_phone'],
  ];

  const handleNext = async () => {
    const fields = stepFieldNames[currentStep];
    if (fields.length > 0) {
      try {
        await form.validateFields(fields);
      } catch {
        return;
      }
    }
    if (currentStep === 2 && totalPassengers < 1) {
      message.error(t('booking.passengerMinOne'));
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  const handleSubmit = async () => {
    if (!orgId) {
      message.error(t('booking.orgNotFound'));
      return;
    }
    if (totalPassengers < 1) {
      message.error(t('booking.passengerCountMin'));
      return;
    }

    try {
      const values = await form.validateFields([
        'date', 'pickup_time', 'pickup_address', 'dropoff_address', 'special_notes',
        'contact_person', 'contact_phone', 'leg_notes', 'vehicle_preference',
      ]);
      // Step fields may be unmounted; preserved store still holds vehicle_preference.
      const vehiclePreference =
        (typeof values.vehicle_preference === 'string' && values.vehicle_preference) ||
        (typeof form.getFieldValue('vehicle_preference') === 'string'
          ? form.getFieldValue('vehicle_preference')
          : null) ||
        'standard';
      setSubmitting(true);

      const geopoints = await resolveTripGeopointsStrict(
        {
          address: values.pickup_address,
          lat: pickupCoords?.lat,
          lng: pickupCoords?.lng,
        },
        {
          address: values.dropoff_address,
          lat: dropoffCoords?.lat,
          lng: dropoffCoords?.lng,
        }
      );

      const pickupTime = dayjs.isDayjs(values.pickup_time)
        ? values.pickup_time.format('HH:mm:ss')
        : typeof values.pickup_time === 'string' && values.pickup_time.length === 5
          ? `${values.pickup_time}:00`
          : values.pickup_time;
      const scheduleDate = dayjs.isDayjs(values.date)
        ? values.date.format('YYYY-MM-DD')
        : values.date;

      const pickupPt = parseGeoPoint(geopoints.pickup_geopoint);
      const dropoffPt = parseGeoPoint(geopoints.dropoff_geopoint);
      if (!pickupPt || !dropoffPt) {
        throw new Error(t('booking.coordsError'));
      }
      const legTimes = buildClientTransferLegTimes({
        pickupTime: String(pickupTime),
        pickup: pickupPt,
        dropoff: dropoffPt,
      });
      const shortPickup = geopoints.pickup_address.split(',')[0]?.trim() || 'Pickup';
      const shortDropoff = geopoints.dropoff_address.split(',')[0]?.trim() || 'Drop-off';
      const title = `${shortPickup} → ${shortDropoff}`;

      const groupsPayload = passengerGroups.map(g => ({
        name: g.name,
        count: g.count,
        lead: g.lead_name,
        phone: g.lead_phone,
      }));

      const notes =
        (typeof values.special_notes === 'string' ? values.special_notes : '').trim() || null;

      const legUserNotes =
        (typeof values.leg_notes === 'string' ? values.leg_notes : '').trim() || null;
      const legNotes = [
        legUserNotes,
        `Vehicle preference: ${vehiclePreference}`,
        `Passenger groups: ${JSON.stringify(groupsPayload)}`,
      ]
        .filter(Boolean)
        .join('\n');

      const contactPerson =
        (typeof values.contact_person === 'string' ? values.contact_person : '').trim() || null;
      const contactPhone =
        (typeof values.contact_phone === 'string' ? values.contact_phone : '').trim() || null;

      const { data: jobData, error: jobError } = await supabase
        .from('jobs')
        .insert([{
          org_id: orgId,
          schedule_date: scheduleDate,
          job_type: 'transfer',
          title,
          passenger_count: totalPassengers,
          status: 'unscheduled',
          source: 'client_request',
          acceptance_status: 'pending',
          notes,
          contact_person: contactPerson,
          contact_phone: contactPhone,
        }])
        .select('id')
        .single();

      if (jobError || !jobData) throw jobError ?? new Error('Failed to create request');

      const { error: legError } = await supabase.from('job_legs').insert([{
        job_id: jobData.id,
        sequence: 1,
        leg_type: 'transfer',
        pickup_address: geopoints.pickup_address,
        dropoff_address: geopoints.dropoff_address,
        pickup_geopoint: geopoints.pickup_geopoint,
        dropoff_geopoint: geopoints.dropoff_geopoint,
        earliest_start: legTimes.earliest_start,
        latest_start: legTimes.latest_start,
        end_time: legTimes.end_time,
        service_duration_minutes: legTimes.service_duration_minutes,
        notes: legNotes || null,
      }]);

      if (legError) throw legError;

      setCreatedRequestId(jobData.id);
      setCompleted(true);
      message.success(t('booking.submitSuccess'));
    } catch (err: unknown) {
      if (err instanceof TripGeocodeError) {
        message.error(err.message);
      } else {
        message.error(t('booking.submitFailed', {
          message: err instanceof Error ? err.message : 'Unknown error',
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setCurrentStep(0);
    setCompleted(false);
    setCreatedRequestId(null);
    setPassengerGroups([
      { key: 0, name: t('booking.mainGroup'), count: 20, lead_name: '', lead_phone: '' }
    ]);
  };

  if (completed) {
    return (
      <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
        <Result
          status="success"
          title={t('booking.successTitle')}
          subTitle={t('booking.successSubtitle')}
          extra={[
            <Button type="primary" key="new" onClick={handleReset} icon={<PlusOutlined />}>
              {t('booking.newRequest')}
            </Button>,
            <Button
              key="view"
              onClick={() =>
                navigate(createdRequestId ? `/requests/${createdRequestId}` : '/requests')
              }
            >
              {t('booking.viewRequest')}
            </Button>
          ]}
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      <div className="client-page-header">
        <div>
          <Title level={3} className="client-page-title">{t('booking.title')}</Title>
          <Text className="client-page-subtitle">
            {t('booking.subtitle')}
          </Text>
        </div>
      </div>

      <Steps
        current={currentStep}
        onChange={setCurrentStep}
        style={{ marginBottom: 32 }}
        items={[
          { title: t('booking.steps.tripDetails'), icon: <CarOutlined /> },
          { title: t('booking.steps.pickupDropoff'), icon: <EnvironmentOutlined /> },
          { title: t('booking.steps.passengers'), icon: <TeamOutlined /> },
          { title: t('booking.steps.review'), icon: <CheckCircleOutlined /> }
        ]}
      />

      <Card style={{ borderRadius: 12 }}>
        <Form
          form={form}
          layout="vertical"
          preserve
          initialValues={{
            date: dayjs().add(1, 'day'),
            pickup_time: dayjs('09:00', 'HH:mm'),
            vehicle_preference: 'standard'
          }}
        >
          {currentStep === 0 && (
            <div>
              <Form.Item
                name="date"
                label={t('booking.tripDate')}
                rules={[{ required: true, message: t('booking.selectDate') }]}
              >
                <DatePicker style={{ width: '100%' }} disabledDate={(d) => d && d < dayjs().startOf('day')} />
              </Form.Item>

              <Form.Item
                name="pickup_time"
                label={t('booking.pickupTime')}
                rules={[{ required: true, message: t('booking.selectPickupTime') }]}
              >
                <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="vehicle_preference" label={t('booking.vehiclePreference')}>
                <Select options={vehicleOptions} />
              </Form.Item>

              <Form.Item
                name="contact_person"
                label={t('common.contact')}
                rules={[{ required: true, whitespace: true, message: t('booking.contactNameRequired') }]}
              >
                <Input placeholder={t('booking.contactName')} allowClear />
              </Form.Item>

              <Form.Item
                name="contact_phone"
                label={t('common.phone')}
                rules={[{ required: true, whitespace: true, message: t('booking.contactPhoneRequired') }]}
              >
                <Input placeholder={t('booking.contactPhone')} allowClear />
              </Form.Item>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Form.Item
                name="pickup_address"
                label={t('booking.pickupAddress')}
                extra={t('booking.pickupAddressExtra')}
                rules={[{ required: true, message: t('booking.pickupAddressRequired') }]}
              >
                <AddressField
                  placeholder={t('booking.pickupPlaceholder')}
                  coordinates={pickupCoords}
                  onCoordinatesChange={setPickupCoords}
                />
              </Form.Item>

              <Form.Item
                name="dropoff_address"
                label={t('booking.dropoffAddress')}
                extra={t('booking.dropoffAddressExtra')}
                rules={[{ required: true, message: t('booking.dropoffAddressRequired') }]}
              >
                <AddressField
                  placeholder={t('booking.dropoffPlaceholder')}
                  coordinates={dropoffCoords}
                  onCoordinatesChange={setDropoffCoords}
                />
              </Form.Item>

              <Form.Item name="special_notes" label={t('booking.specialRequirements')}>
                <TextArea
                  rows={3}
                  placeholder={t('booking.specialRequirementsPlaceholder')}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <Card
                size="small"
                style={{ marginBottom: 16, background: '#f6ffed', borderColor: '#b7eb8f' }}
              >
                <div style={{ textAlign: 'center' }}>
                  <Text style={{ fontSize: 14 }}>{t('booking.totalPassengers')} </Text>
                  <Text strong style={{ fontSize: 28, color: '#52c41a' }}>{totalPassengers}</Text>
                </div>
              </Card>

              {passengerGroups.map((group) => (
                <Card
                  key={group.key}
                  size="small"
                  title={group.name}
                  style={{ marginBottom: 12, borderRadius: 8 }}
                  extra={
                    passengerGroups.length > 1 && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<MinusCircleOutlined />}
                        onClick={() => removeGroup(group.key)}
                      >
                        {t('booking.removeGroup')}
                      </Button>
                    )
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                      addonBefore={t('booking.groupName')}
                      value={group.name}
                      onChange={(e) => updateGroup(group.key, 'name', e.target.value)}
                      placeholder={t('booking.groupNamePlaceholder')}
                    />
                    <InputNumber
                      addonBefore={t('booking.groupPassengers')}
                      min={1}
                      max={200}
                      value={group.count}
                      onChange={(val) => updateGroup(group.key, 'count', val || 0)}
                      style={{ width: '100%' }}
                    />
                    <Input
                      addonBefore={t('booking.groupLead')}
                      value={group.lead_name}
                      onChange={(e) => updateGroup(group.key, 'lead_name', e.target.value)}
                      placeholder={t('booking.groupLeadPlaceholder')}
                    />
                    <Input
                      addonBefore={t('common.phone')}
                      value={group.lead_phone}
                      onChange={(e) => updateGroup(group.key, 'lead_phone', e.target.value)}
                      placeholder="+614XXXXXXXX"
                    />
                  </Space>
                </Card>
              ))}

              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                onClick={addGroup}
                style={{ marginTop: 8 }}
              >
                {t('booking.addPassengerGroup')}
              </Button>

              <Form.Item
                name="leg_notes"
                label={t('booking.legNotes')}
                style={{ marginTop: 16 }}
                extra={t('booking.legNotesExtra')}
              >
                <TextArea
                  rows={3}
                  placeholder={t('booking.legNotesPlaceholder')}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <Card size="small" title={t('booking.reviewTripDetails')} style={{ marginBottom: 12 }}>
                <Space direction="vertical">
                  <Text><strong>{t('booking.reviewDate')}</strong> {form.getFieldValue('date')?.format?.('dddd, DD MMMM YYYY') || t('common.noData')}</Text>
                  <Text><strong>{t('booking.reviewPickupTime')}</strong> {form.getFieldValue('pickup_time')?.format?.('h:mm A') || t('common.noData')}</Text>
                  <Text><strong>{t('booking.reviewVehicle')}</strong> {vehicleLabel(form.getFieldValue('vehicle_preference') || 'standard')}</Text>
                  {(form.getFieldValue('contact_person') || form.getFieldValue('contact_phone')) && (
                    <Text>
                      <strong>{t('booking.reviewContact')}</strong>{' '}
                      {[form.getFieldValue('contact_person'), form.getFieldValue('contact_phone')]
                        .filter(Boolean)
                        .join(' · ') || t('common.noData')}
                    </Text>
                  )}
                </Space>
              </Card>

              <Card size="small" title={t('booking.reviewLocations')} style={{ marginBottom: 12 }}>
                <Space direction="vertical">
                  <Text><strong>{t('booking.reviewPickup')}</strong> {form.getFieldValue('pickup_address') || t('common.noData')}</Text>
                  <Text><strong>{t('booking.reviewDropoff')}</strong> {form.getFieldValue('dropoff_address') || t('common.noData')}</Text>
                </Space>
              </Card>

              <Card size="small" title={t('booking.reviewPassengers')} style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                  {t('booking.reviewTotalPassengers', { count: totalPassengers })}
                </Text>
                <Divider style={{ margin: '8px 0' }} />
                {passengerGroups.map(g => (
                  <div key={g.key} style={{ marginBottom: 8 }}>
                    <Text>{t('booking.reviewGroupPassengers', { name: g.name, count: g.count })}</Text>
                    {g.lead_name && <Text type="secondary">{t('booking.reviewLead', { name: g.lead_name })}</Text>}
                  </div>
                ))}
                {form.getFieldValue('leg_notes') && (
                  <>
                    <Divider style={{ margin: '8px 0' }} />
                    <Text type="secondary">
                      <strong>{t('booking.reviewLegNotes')}</strong> {form.getFieldValue('leg_notes')}
                    </Text>
                  </>
                )}
              </Card>

              {form.getFieldValue('special_notes') && (
                <Card size="small" title={t('booking.reviewSpecialRequirements')} style={{ marginBottom: 12 }}>
                  <Text>{form.getFieldValue('special_notes')}</Text>
                </Card>
              )}
            </div>
          )}

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
            {currentStep > 0 ? (
              <Button onClick={() => setCurrentStep(prev => prev - 1)}>{t('common.previous')}</Button>
            ) : (
              <div />
            )}

            {currentStep < 3 ? (
              <Button type="primary" onClick={handleNext}>{t('common.next')}</Button>
            ) : (
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={submitting}
                icon={<CheckCircleOutlined />}
                size="large"
              >
                {t('booking.submitRequest')}
              </Button>
            )}
          </div>
        </Form>
      </Card>
    </div>
  );
}

export default TripBooking;
